/* eslint-disable react-hooks/rules-of-hooks */
import {
  EQueryKeysByAcademicHours,
  EQueryKeysByFacetName,
  EQueryKeysByPrice,
  IProgram,
  IProgramsByTypeFacets,
} from '@ucheba/store/institutions/programs/types'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { EDataKeys } from '@ucheba/utils/types/dataKeys'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from '@ucheba/utils/hooks/useRouter'
import { institutionsProgramTypesSelectors } from '@ucheba/store/institutions/program-types'
import { capitalizeFirstLetter } from '@ucheba/utils/helpers/string'
import { getSingleQuery, stringifyUrl } from '@ucheba/utils/helpers/router'
import { INavigationProps } from '@ucheba/ui/components/Navigation/types'
import {
  institutionsProgramsSelectors,
  institutionsProgramsThunks,
} from '@ucheba/store/institutions/programs'
import { institutionItemSelectors } from '@ucheba/store/institutions/item'
import { institutionProgramsLimit } from '@ucheba/store/institutions/programs/constants'
import { IFacetItemProps } from '@ucheba/ui/components/Facet/types'
import { IFacet } from '@ucheba/utils/types/search'
import { EProgramTypes } from '@ucheba/utils/types/program'
import {
  specialitiesIdSelectors,
  specialitiesIdThunks,
} from '@ucheba/store/specialities/id'
import { extraFacets, sortItems, types } from './constants'
import { getSubjectIcon } from '../../../../../../utils/helpers/getSubjectIcon'
import { IProgramsPageFacets } from './types'
import { getProgramType } from '../[slug]/bll'

interface IUseProgramsPageCore {
  (id: number): {
    items: INavigationProps['items']
    activeIndex: number
    programType: number
    title: string
  }
}

export const useProgramsPageCore: IUseProgramsPageCore = () => {
  const router = useRouter()
  const programTypes = useSelector(institutionsProgramTypesSelectors.programTypes)
  const { params, query, pathname } = router
  const currentInstitution = useSelector(institutionItemSelectors.currentInstitution)

  const programSlug = useMemo(() => {
    if (params.slug) {
      return getSingleQuery(params.slug)
    }

    if (query.slug) {
      return getSingleQuery(query.slug)
    }

    if (!pathname.endsWith('/programs')) {
      const arr = pathname.split('/')

      return arr[arr.length - 1]
    }

    return undefined
  }, [params.slug, pathname, query.slug])

  const id = useMemo(() => {
    if (params.id) {
      return getSingleQuery(params.id)
    }

    if (currentInstitution.id) {
      return String(currentInstitution.id)
    }

    return getSingleQuery(query.id)
  }, [currentInstitution.id, params.id, query.id])

  const programType = useMemo(() => {
    if (programSlug) {
      return getProgramType(programSlug, programTypes)?.value
    }

    if (programTypes && programTypes.length > 0) {
      return programTypes[0].value
    }

    return {}
  }, [programSlug, programTypes])

  const currentProgramType = useMemo(
    () => programTypes.find(({ value }) => value === programType),
    [programType, programTypes]
  )

  const items = useMemo(() => {
    return programTypes.map((item, key) => {
      return {
        slug: String(item.value),
        name: capitalizeFirstLetter(item.name),
        href: `/uz/${id}/programs${key ? `/${item.slug}` : ''}`,
      }
    })
  }, [id, programTypes])

  /** Получаем индекс активного пункта в зависимости от квери параметров */
  const activeIndex = useMemo(() => {
    return programTypes.findIndex(({ value }) => value === programType) || 0
  }, [programSlug, programTypes])

  const title = useMemo(() => {
    const type = currentProgramType?.value

    if (type === EProgramTypes.higher) {
      return `Программы высшего образования`
    }

    if (type === EProgramTypes.vocational) {
      return `Программы среднего профессионального образования`
    }

    if (type === EProgramTypes.mba) {
      return 'Программы MBA'
    }

    return `Программы ${currentProgramType?.genitiveName.toLowerCase() || ''}`
  }, [currentProgramType])

  return {
    items,
    activeIndex,
    programType: Number(programType),
    title,
  }
}

interface IUseProgramsList {
  (
    id: number,
    programType: number
  ): {
    programs: IProgram[] | null
    total: number
    facets: IProgramsByTypeFacets
  }
}

export const useProgramsList: IUseProgramsList = (id, programType) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const { query } = router
  const currentInstitution = useSelector(institutionItemSelectors.currentInstitution)

  // Тут получается что хуки в условной конструкции, но по другому не знаю как делать
  // Если бэк говорит что программ у данного УЗ нет, то вызовы в useEffect не делаем

  if (!currentInstitution.hasPrograms)
    return {
      total: 0,
      programs: [],
      facets: {},
    }

  const programsHight = useSelector(institutionsProgramsSelectors.programsHigher)
  const programsVocational = useSelector(institutionsProgramsSelectors.programsVocational)
  const programsCourse = useSelector(institutionsProgramsSelectors.programsCourse)
  const programsMBA = useSelector(institutionsProgramsSelectors.programsMBA)
  const programsMaster = useSelector(institutionsProgramsSelectors.programsMaster)
  const programsSchool = useSelector(institutionsProgramsSelectors.programsSchool)
  const programsPostgraduate = useSelector(
    institutionsProgramsSelectors.programsPostgraduate
  )
  const programsPreparatory = useSelector(institutionsProgramsSelectors.preparatory)

  const offset = useMemo(() => {
    if (query.page) {
      return Number(query.page) - 1
    }

    return 0
  }, [query])

  const params = useMemo(() => {
    if (query) {
      return Object.keys(query).reduce((acc, queryKey) => {
        const key = queryKey.replace('[]', '')

        if (key === EQueryKeysByFacetName.withoutExams) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          acc[EQueryKeysByFacetName.withoutExams] = query[key] && query[key][0]
        } else if (queryKey.includes('[]') && !Array.isArray(query[queryKey])) {
          acc[key] = [query[queryKey]]
        } else {
          acc[key] = query[queryKey]
        }

        return acc
      }, {})
    }

    return {}
  }, [query])

  const isRender = useRef(false)

  const paramsId = useMemo(() => {
    if (router.params.id) {
      return getSingleQuery(router.params.id)
    }
    return getSingleQuery(query.id)
  }, [router.params.id, query.id])

  useEffect(() => {
    if (isRender.current) {
      if (types.height.includes(programType) && String(id) === String(paramsId)) {
        dispatch(
          institutionsProgramsThunks.fetchProgramsHigher({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }

      if (types.master.includes(programType)) {
        dispatch(
          institutionsProgramsThunks.fetchProgramsMaster({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }

      if (types.postgraduate.includes(programType)) {
        dispatch(
          institutionsProgramsThunks.fetchProgramsPostgraduate({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }

      if (types.vocational.includes(programType) && String(id) === String(paramsId)) {
        dispatch(
          institutionsProgramsThunks.fetchProgramsVocational({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }

      if (types.course.includes(programType) && String(id) === String(paramsId)) {
        dispatch(
          institutionsProgramsThunks.fetchProgramsCourse({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }

      if (types.mba.includes(programType)) {
        dispatch(
          institutionsProgramsThunks.fetchProgramsMBA({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }

      if (types.school.includes(programType)) {
        dispatch(
          institutionsProgramsThunks.fetchProgramsSchool({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }

      if (types.preparatory.includes(programType)) {
        dispatch(
          institutionsProgramsThunks.fetchPreparatory({
            data: {
              institutionIds: id,
              limit: institutionProgramsLimit,
              offset,
              ...params,
            },
          })
        )
      }
    }
    isRender.current = true
  }, [id, programType, query, offset])

  const programs = useMemo(() => {
    if (types.height.includes(programType)) {
      return programsHight
    }

    if (types.master.includes(programType)) {
      return programsMaster
    }

    if (types.postgraduate.includes(programType)) {
      return programsPostgraduate
    }

    if (types.vocational.includes(programType)) {
      return programsVocational
    }

    if (types.course.includes(programType)) {
      return programsCourse
    }

    if (types.mba.includes(programType)) {
      return programsMBA
    }

    if (types.school.includes(programType)) {
      return programsSchool
    }

    if (types.preparatory.includes(programType)) {
      return programsPreparatory
    }

    return null
  }, [
    programType,
    programsHight,
    programsSchool,
    programsCourse,
    programsMBA,
    programsVocational,
    programsPostgraduate,
    programsMaster,
    programsPreparatory,
  ])

  return {
    total: programs?.total || 0,
    programs: programs?.items || null,
    facets: programs?.facets || {},
  }
}

interface IUseSubmitRangeFacet {
  (from: string, to: string): (min: number, max: number) => void
}

export const useSubmitRangeFacet: IUseSubmitRangeFacet = (from, to) => {
  const router = useRouter()
  const { query, asPath } = router

  return useCallback(
    (min: number, max: number) => {
      const exclude: string[] = ['page', 'query']

      router.push(
        stringifyUrl(asPath, {
          update: {
            ...query,
            [from]: min,
            [to]: max,
          },
          exclude,
        }),
        undefined,
        {
          shallow: true,
        }
      )
    },
    [from, to, query, asPath]
  )
}

export const useExamsWithIcons = (
  exams: IProgramsPageFacets['facets']['examSubjects']
): IFacetItemProps['items'] => {
  return useMemo(() => {
    if (!exams) return []

    return exams.map((item) => {
      return {
        ...item,
        Icon: item.slug ? getSubjectIcon(item.slug) : null,
      }
    })
  }, [exams])
}

export const useFacetsKeys = (
  facets: IProgramsByTypeFacets
): Record<string, IFacet | IFacet[]> => {
  const { query } = useRouter()
  const dispatch = useDispatch()
  const specialitieMin = useSelector(specialitiesIdSelectors.min)
  const specialitieMinItems = useSelector(specialitiesIdSelectors.minItems)

  const specialityIds = useMemo(() => {
    if (query.specialityIds) {
      if (Array.isArray(query.specialityIds)) {
        return query.specialityIds.map((id) => Number(id))
      }
      return Number(query.specialityIds)
    }

    return null
  }, [query])

  const speciality = useMemo(() => {
    return specialitieMin
  }, [specialitieMin])

  const specialityItems = useMemo(() => {
    return specialitieMinItems
  }, [specialitieMinItems])

  const fetchSpecialitiesById = useCallback(() => {
    if (specialityIds && Array.isArray(specialityIds)) {
      dispatch(specialitiesIdThunks.fetchMinItems({ data: { ids: specialityIds } }))
    } else if (specialityIds) {
      dispatch(specialitiesIdThunks.fetchMin({ data: { id: Number(specialityIds) } }))
    }
  }, [dispatch, specialityIds])

  useEffect(() => {
    fetchSpecialitiesById()
  }, [fetchSpecialitiesById])

  return useMemo(() => {
    const newFacets: Record<string, IFacet[]> = {}

    Object.keys(facets).forEach((item) => {
      if (item === 'price') {
        newFacets[EQueryKeysByPrice.priceFrom] = [
          {
            name: 'Стоимость от',
            value: 1,
            count: 1,
          },
        ]

        newFacets[EQueryKeysByPrice.priceTo] = [
          {
            name: 'Стоимость до',
            value: 2,
            count: 1,
          },
        ]
      } else if (item === 'academicHours') {
        newFacets[EQueryKeysByAcademicHours.academicHoursFrom] = [
          {
            name: 'Объем от',
            value: 1,
            count: 1,
          },
        ]

        newFacets[EQueryKeysByAcademicHours.academicHoursTo] = [
          {
            name: 'Объем до',
            value: 2,
            count: 1,
          },
        ]
      } else {
        newFacets[EQueryKeysByFacetName[item]] = [...facets[item]]
      }
    })

    if (specialityIds && speciality) {
      newFacets[EQueryKeysByFacetName.specialityIds] = [
        { name: speciality.name, value: String(specialityIds), count: 1 },
      ]
    }
    if (specialityIds && specialityItems) {
      newFacets[EQueryKeysByFacetName.specialityIds] = specialityItems.map((el) => {
        return { name: el.name, value: String(el.id), count: 1 }
      })
    }
    return { ...newFacets, ...extraFacets }
  }, [facets, speciality, specialityItems, specialityIds])
}

export const useSelectedFacetsCount = (
  keys: Record<string, IFacet | IFacet[]>
): number => {
  const router = useRouter()
  const { query } = router

  const filteredKeys = Object.keys(query).filter((queryKey, key, arr) => {
    const facetName = queryKey.replace(/[[]]/gm, '')

    if (facetName === EDataKeys.priceTo) {
      return !arr.includes(EDataKeys.priceFrom)
    }
    if (facetName === EDataKeys.academicHoursTo) {
      return !arr.includes(EDataKeys.academicHoursFrom)
    }

    // рубрики не учитываются при подсчете
    if (facetName === EQueryKeysByFacetName.tags) return false

    return keys[facetName]
  })

  return filteredKeys.length
}

interface IUseSortItems {
  (programType: number): {
    key: string
    direction: string
    name: string
    types?: number[]
  }[]
}

export const useSortItems: IUseSortItems = (programType: number) => {
  return useMemo(() => {
    return sortItems.filter((item) => {
      if (!item.types) return true

      return item.types.includes(programType)
    })
  }, [programType])
}
